import apiClient from "./apiClient";
import i18n from "../../i18n";

const getAcceptLanguage = () =>
  i18n.language?.includes("ru") ? "ru-RU" : "kk-KZ";

const headers = () => ({
  "Content-Type": "application/json",
  "Accept-Language": getAcceptLanguage(),
});

const BASE = () =>
  `${process.env.REACT_APP_URL_AUTH_SERVICE}/reports/participants-registry`;

export type ParticipantsRegistryReportStatus = "FORMING" | "FORMED" | "ERROR";

export type ParticipantsRegistrySort =
  | "REGISTRATION_DATE"
  | "ORGANIZATION_NAME"
  | "CONTRACT_DATE";

export type ParticipantsRegistryReportFilter = {
  // Пусто — все подключённые товарные группы
  productGroupIds?: number[];
  organizationId?: number;
  registrationDateFrom?: string;
  registrationDateTo?: string;
  contractDateFrom?: string;
  contractDateTo?: string;
  city?: string;
  sort?: ParticipantsRegistrySort;
};

export type ParticipantsRegistryReportFile = {
  format: "XLSX";
  fileName?: string;
  sizeBytes?: number;
};

export type ParticipantsRegistryReportItem = {
  id: number;
  reportNumber?: string;
  filters?: ParticipantsRegistryReportFilter;
  status: ParticipantsRegistryReportStatus;
  requestedBy?: number;
  requestedByName?: string;
  requestedAt?: string;
  startedAt?: string;
  completedAt?: string;
  rowsCount?: number;
  errorMessage?: string;
  files?: ParticipantsRegistryReportFile[];
};

export type ParticipantsRegistryReportListResponse = {
  reports: ParticipantsRegistryReportItem[];
  totalItems?: number;
  totalPages?: number;
  page?: number;
  size?: number;
};

export type ParticipantsRegistryReportListParams = {
  status?: ParticipantsRegistryReportStatus;
  reportNumber?: string;
  createdFrom?: string;
  createdTo?: string;
};

export type ParticipantsRegistryReportRow = {
  rowNumber?: number;
  iinBin?: string;
  organizationName?: string;
  position?: string;
  fullName?: string;
  email?: string;
  phone?: string;
  region?: string;
  legalAddress?: string;
  productGroup?: string;
  // Даты приходят уже в формате ДД.ММ.ГГГГ
  registrationDate?: string;
  contractNumber?: string;
  contractDate?: string;
  iban?: string;
  bankName?: string;
  dcbAccount?: string;
  globalAccount?: string;
  participantRoles?: string;
};

export type ParticipantsRegistryReportPreviewResponse = {
  rows: ParticipantsRegistryReportRow[];
  totalItems?: number;
  totalPages?: number;
  page?: number;
  size?: number;
};

export const searchParticipantsRegistryReports = async (
  page: number = 1,
  size: number = 20,
  params: ParticipantsRegistryReportListParams = {},
): Promise<ParticipantsRegistryReportListResponse> => {
  const query = new URLSearchParams({
    page: String(page),
    size: String(size),
  });
  Object.entries(params).forEach(([key, value]) => {
    if (value) query.set(key, value);
  });

  const response = await apiClient.get(`${BASE()}?${query.toString()}`, {
    headers: headers(),
    withCredentials: true,
  });
  return response.data;
};

export const createParticipantsRegistryReport = async (
  payload: ParticipantsRegistryReportFilter,
): Promise<ParticipantsRegistryReportItem> => {
  const response = await apiClient.post(BASE(), payload, {
    headers: headers(),
    withCredentials: true,
  });
  return response.data;
};

export const getParticipantsRegistryReport = async (
  reportId: number,
): Promise<ParticipantsRegistryReportItem> => {
  const response = await apiClient.get(`${BASE()}/${reportId}`, {
    headers: headers(),
    withCredentials: true,
  });
  return response.data;
};

export const previewParticipantsRegistryReport = async (
  filters: ParticipantsRegistryReportFilter,
  page: number = 1,
  size: number = 20,
): Promise<ParticipantsRegistryReportPreviewResponse> => {
  const query = new URLSearchParams({
    page: String(page),
    size: String(size),
  });

  const response = await apiClient.post(
    `${BASE()}/preview?${query.toString()}`,
    filters,
    {
      headers: headers(),
      withCredentials: true,
    },
  );
  return response.data;
};

export const downloadParticipantsRegistryReport = async (
  reportId: number,
): Promise<Blob> => {
  const response = await apiClient.get(`${BASE()}/${reportId}/download`, {
    headers: {
      "Accept-Language": getAcceptLanguage(),
    },
    withCredentials: true,
    responseType: "blob",
  });
  return response.data;
};
